/*
 * Synthetic launch model — demo only.
 *
 * Every KPI is an additive utility: audience baseline + one effect per selected
 * factor option (+ audience-specific adjustments) + a handful of pairwise
 * interactions. The reference option of each factor has a zero effect, so the
 * BASELINE_CONFIGURATION yields the audience baseline exactly. The full
 * factorial is small enough (< 200k scenarios) to brute-force in the browser;
 * the model is compiled to flat numeric tables per audience to keep that fast.
 */

import {
  Audience,
  AudienceId,
  Configuration,
  Effect,
  Factor,
  FactorGroup,
  FactorId,
  KpiDefinition,
  KpiId,
  Kpis,
  OptimizationResult,
} from "./types";

/* ── Definitions ────────────────────────────────── */

export const AUDIENCES: Audience[] = [
  {
    description: "Nationally representative, 18–65",
    id: "all",
    label: "All US consumers",
  },
  { description: "Ages 18–27", id: "genz", label: "Gen Z" },
  { description: "Ages 28–43", id: "millennials", label: "Millennials" },
  {
    description: "Households with children under 12",
    id: "parents",
    label: "Parents",
  },
  {
    description: "Bought Nutella in the last 3 months",
    id: "nutellaBuyers",
    label: "Current Nutella buyers",
  },
  {
    description: "Peanut butter 3+ times a week",
    id: "pbHeavy",
    label: "Heavy peanut butter buyers",
  },
];

export const BASELINE_CONFIGURATION: Configuration = {
  advertising: "social",
  claim: "noArtificial",
  packSize: "16",
  packaging: "nutellaLed",
  positioning: "family",
  price: "5.99",
  retail: "walmart",
};

export const FACTOR_GROUPS: { id: FactorGroup; label: string }[] = [
  { id: "product", label: "Product" },
  { id: "goToMarket", label: "Go-to-market" },
];

export const FACTORS: Factor[] = [
  {
    group: "goToMarket",
    id: "price",
    label: "Shelf price",
    options: [
      { label: "$4.99", value: "4.99" },
      { label: "$5.49", value: "5.49" },
      { label: "$5.99", value: "5.99" },
      { label: "$6.49", value: "6.49" },
    ],
  },
  {
    group: "product",
    id: "packSize",
    label: "Jar size",
    options: [
      { label: "12 oz", value: "12" },
      { label: "16 oz", value: "16" },
      { label: "20 oz", value: "20" },
    ],
  },
  {
    group: "product",
    id: "packaging",
    label: "Pack design",
    options: [
      {
        description:
          "Classic Nutella jar: white lid, hero wordmark, bread and milk; roasted peanuts on the label and a “peanut spread with cocoa” line.",
        label: "Nutella only",
        value: "nutellaOnly",
      },
      {
        description:
          "Classic Nutella label and lid; a golden “PEANUT SPREAD” band and roasted peanuts signal the twist.",
        label: "Mostly Nutella",
        value: "nutellaLed",
      },
      {
        description:
          "The launched Nutella Peanut look: yellow lid, tan label, Nutella wordmark with a “peanut” script.",
        label: "Co-branded",
        value: "balanced",
      },
      {
        description:
          "Peanut codes lead: brown lid, kraft label, “PEANUT SPREAD” as the title under the Nutella wordmark.",
        label: "Mostly peanut",
        value: "peanutLed",
      },
      {
        description:
          "Peanut-first: black lid, cream label, a pile of peanuts as the hero under “PEANUT SPREAD”; Nutella wordmark kept as the signature.",
        label: "Peanut only",
        value: "peanutOnly",
      },
    ],
  },
  {
    group: "goToMarket",
    id: "positioning",
    label: "Campaign idea",
    options: [
      {
        description:
          "Hero the taste. Creative built on weekend-breakfast and dessert moments; brand platform stays Nutella's joy-of-indulgence; media skews video (TV, streaming, social).",
        label: "Indulgent treat",
        value: "indulgence",
      },
      {
        description:
          "The jar the whole family agrees on. Creative around busy mornings and lunchboxes; platform extends Nutella's breakfast heritage; media skews parents (social, retail media).",
        label: "Family breakfast staple",
        value: "family",
      },
      {
        description:
          "Fuel, not a treat. Creative around post-workout and snacking; a new performance platform for Nutella; media skews influencers and social.",
        label: "Protein-packed snack",
        value: "protein",
      },
      {
        description:
          "Nothing to hide. Creative on ingredient honesty and real roasted peanuts; a transparency platform; media skews social and natural-channel retail media.",
        label: "Simple, real ingredients",
        value: "natural",
      },
    ],
  },
  {
    group: "product",
    id: "claim",
    label: "Front-of-pack claim",
    options: [
      {
        description:
          "Clean front of pack: brand, product name and visuals only. Nothing to substantiate.",
        label: "No claim",
        value: "none",
      },
      {
        description: "Ingredient claim; substantiated by the recipe alone.",
        label: "Made with real roasted peanuts",
        value: "realPeanuts",
      },
      {
        description:
          "Absence claim; the current Nutella recipe already qualifies.",
        label: "No artificial colors or flavors",
        value: "noArtificial",
      },
      {
        description:
          "Nutrition claim; requires the protein content to be validated per serving and FDA-compliant wording.",
        label: "7g of protein per serving",
        value: "sourceOfProtein",
      },
      {
        description: "Usage claim; anchors the jar in the breakfast occasion.",
        label: "Made for family breakfasts",
        value: "breakfast",
      },
    ],
  },
  {
    group: "goToMarket",
    id: "advertising",
    label: "Media plan",
    members: [
      { id: "social", label: "Paid social" },
      { id: "influencer", label: "Influencers" },
      { id: "tv", label: "TV" },
      { id: "retailMedia", label: "Retail media" },
    ],
    options: [
      { label: "Paid social", members: ["social"], value: "social" },
      { label: "TV", members: ["tv"], value: "tv" },
      {
        label: "Influencers + paid social",
        members: ["influencer", "social"],
        value: "influencer",
      },
      { label: "Retail media", members: ["retailMedia"], value: "retailMedia" },
      {
        label: "Paid social + retail media",
        members: ["social", "retailMedia"],
        value: "socialRetailMedia",
      },
      {
        label: "TV + paid social",
        members: ["tv", "social"],
        value: "tvSocial",
      },
    ],
  },
  {
    group: "goToMarket",
    id: "retail",
    label: "Retail distribution",
    members: [
      { id: "walmart", label: "Walmart" },
      { id: "target", label: "Target" },
      { id: "kroger", label: "Kroger" },
      { id: "costco", label: "Costco" },
      { id: "wholeFoods", label: "Whole Foods" },
      { id: "sprouts", label: "Sprouts" },
    ],
    options: [
      { label: "Walmart", members: ["walmart"], value: "walmart" },
      { label: "Target", members: ["target"], value: "target" },
      {
        label: "Walmart + Target",
        members: ["walmart", "target"],
        value: "walmartTarget",
      },
      { label: "Kroger", members: ["kroger"], value: "kroger" },
      {
        label: "Whole Foods + Sprouts",
        members: ["wholeFoods", "sprouts"],
        value: "wholeFoods",
      },
      {
        label: "Walmart + Target + Kroger + Costco",
        members: ["walmart", "target", "kroger", "costco"],
        value: "mass",
      },
    ],
  },
];

export const KPIS: KpiDefinition[] = [
  {
    description:
      "Share of buyers who would definitely or probably buy the product (top-2 box on a 5-point scale).",
    format: "percent",
    id: "purchaseIntent",
    label: "Purchase intent",
  },
  {
    description:
      "Average price per jar that buyers consider acceptable for this configuration. Compare it with the selected shelf price.",
    format: "currency",
    id: "wtp",
    label: "Willingness to pay",
  },
  {
    description:
      "Overall liking of the concept, rescaled to a 0–100 index (100 = every buyer rates it very appealing).",
    format: "score",
    id: "appeal",
    label: "Appeal",
  },
  {
    description:
      "How well buyers feel the product belongs to the Nutella brand, on a 0–100 index. Low fit signals a risk of diluting brand equity.",
    format: "score",
    id: "brandFit",
    label: "Brand fit",
  },
  {
    description:
      "Share of buyers expected to know about the launch after six months, given the media plan and retail presence.",
    format: "percent",
    id: "awareness",
    label: "Awareness",
  },
  {
    description:
      "Share of buyers expected to purchase at least once in the first year (aware × available × intent).",
    format: "percent",
    id: "trial",
    label: "Trial",
  },
];

export const SCENARIO_COUNT = FACTORS.reduce(
  (count, factor) => count * factor.options.length,
  1
);

/* ── Model parameters ───────────────────────────── */

type AudienceAdjustments = Partial<Record<FactorId, Record<string, Effect>>>;

interface Interaction {
  audiences?: AudienceId[];
  effect: Effect;
  note: string;
  when: Partial<Configuration>;
}

interface CompiledAudience {
  baseline: number[];
  /** factor index → option index → KPI vector */
  effects: number[][][];
  interactions: { effect: number[]; when: [number, number][] }[];
}

const AUDIENCE_ADJUSTMENTS: Record<AudienceId, AudienceAdjustments> = {
  all: {},
  genz: {
    advertising: {
      influencer: { appeal: 2, awareness: 6, purchaseIntent: 3, trial: 3 },
      retailMedia: { awareness: -2, purchaseIntent: -1 },
      tv: { awareness: -5, purchaseIntent: -2 },
    },
    claim: {
      breakfast: { appeal: -2, purchaseIntent: -3 },
      sourceOfProtein: { appeal: 3, purchaseIntent: 5 },
    },
    packaging: {
      balanced: { appeal: 2, purchaseIntent: 2 },
      nutellaOnly: { appeal: -2, brandFit: -3, purchaseIntent: -3 },
      peanutLed: { purchaseIntent: 2 },
    },
    packSize: { "12": { purchaseIntent: 2 }, "20": { purchaseIntent: -3 } },
    positioning: {
      family: { appeal: -2, purchaseIntent: -4 },
      protein: { appeal: 5, brandFit: 5, purchaseIntent: 9 },
    },
    price: {
      "4.99": { purchaseIntent: 2 },
      "5.99": { purchaseIntent: -2 },
      "6.49": { purchaseIntent: -4 },
    },
  },
  millennials: {
    advertising: {
      influencer: { purchaseIntent: 1 },
      tv: { purchaseIntent: -1 },
    },
    claim: { realPeanuts: { purchaseIntent: 1 } },
    positioning: { natural: { brandFit: 2, purchaseIntent: 3 } },
    price: { "4.99": { brandFit: -1, purchaseIntent: -1 } },
    retail: {
      mass: { purchaseIntent: -1 },
      target: { awareness: 2, purchaseIntent: 2 },
      wholeFoods: { awareness: 3, purchaseIntent: 1 },
    },
  },
  nutellaBuyers: {
    packaging: {
      balanced: { appeal: -2, brandFit: -3, purchaseIntent: -3 },
      nutellaOnly: { brandFit: 2, purchaseIntent: 3 },
      peanutLed: { appeal: -3, brandFit: -6, purchaseIntent: -5 },
      peanutOnly: { appeal: -5, brandFit: -8, purchaseIntent: -9 },
    },
    packSize: { "12": { purchaseIntent: -1 } },
    positioning: {
      indulgence: { brandFit: 3, purchaseIntent: 4 },
      protein: { brandFit: -6, purchaseIntent: -5 },
    },
    price: {
      "4.99": { brandFit: -2, purchaseIntent: -2 },
      "5.49": { brandFit: -1, purchaseIntent: -2, wtp: -0.1 },
      "6.49": { purchaseIntent: 2 },
    },
    retail: { wholeFoods: { purchaseIntent: -1 } },
  },
  parents: {
    claim: {
      breakfast: { brandFit: 2, purchaseIntent: 3 },
      sourceOfProtein: { purchaseIntent: -2 },
    },
    packSize: {
      "12": { purchaseIntent: -2 },
      "20": { purchaseIntent: 4, trial: 2, wtp: 0.1 },
    },
    positioning: {
      family: { appeal: 3, purchaseIntent: 3 },
      indulgence: { purchaseIntent: -1 },
      natural: { purchaseIntent: -1 },
      protein: { brandFit: -2 },
    },
    price: { "6.49": { purchaseIntent: -3 } },
    retail: { mass: { purchaseIntent: 1 }, walmart: { purchaseIntent: 1 } },
  },
  pbHeavy: {
    claim: {
      breakfast: { purchaseIntent: -1 },
      realPeanuts: { appeal: 3, purchaseIntent: 4 },
      sourceOfProtein: { purchaseIntent: 2 },
    },
    packaging: {
      balanced: { appeal: 2, brandFit: 3, purchaseIntent: 2 },
      nutellaLed: { appeal: -2, purchaseIntent: -3 },
      nutellaOnly: { appeal: -4, purchaseIntent: -5 },
      peanutLed: { appeal: 6, brandFit: 8, purchaseIntent: 8 },
      peanutOnly: { appeal: 7, brandFit: 10, purchaseIntent: 9 },
    },
    packSize: { "20": { purchaseIntent: 2, trial: 1 } },
    positioning: {
      indulgence: { purchaseIntent: -3 },
      protein: { appeal: 2, purchaseIntent: 3 },
    },
    price: { "4.99": { purchaseIntent: 2 }, "6.49": { purchaseIntent: -3 } },
    retail: { kroger: { purchaseIntent: 1 } },
  },
};

const BASELINES: Record<AudienceId, Kpis> = {
  all: {
    appeal: 52,
    awareness: 14,
    brandFit: 62,
    purchaseIntent: 22,
    revenue: 100,
    trial: 6,
    wtp: 5.32,
  },
  // Curious but cash-constrained; the brand means less to them.
  genz: {
    appeal: 54,
    awareness: 17,
    brandFit: 53,
    purchaseIntent: 19,
    revenue: 100,
    trial: 6,
    wtp: 4.9,
  },
  millennials: {
    appeal: 53,
    awareness: 15,
    brandFit: 61,
    purchaseIntent: 24,
    revenue: 100,
    trial: 7,
    wtp: 5.45,
  },
  nutellaBuyers: {
    appeal: 62,
    awareness: 23,
    brandFit: 70,
    purchaseIntent: 35,
    revenue: 100,
    trial: 14,
    wtp: 5.65,
  },
  parents: {
    appeal: 55,
    awareness: 16,
    brandFit: 64,
    purchaseIntent: 26,
    revenue: 100,
    trial: 8,
    wtp: 5.28,
  },
  // Loyal to their peanut butter; a sweet spread is a hard sell.
  pbHeavy: {
    appeal: 44,
    awareness: 12,
    brandFit: 48,
    purchaseIntent: 14,
    revenue: 100,
    trial: 3,
    wtp: 4.95,
  },
};

/** Relative distribution weight (doors × channel margin), Walmart = 1. */
const DISTRIBUTION: Record<string, number> = {
  kroger: 0.7,
  mass: 1.3,
  target: 0.75,
  walmart: 1,
  walmartTarget: 1.25,
  wholeFoods: 0.4,
};

/** Main effect of every option, for all audiences. Reference options are 0. */
const EFFECTS: Record<FactorId, Record<string, Effect>> = {
  advertising: {
    influencer: { appeal: 1, awareness: 2, brandFit: -1 },
    retailMedia: { awareness: 3, purchaseIntent: 1, trial: 4 },
    social: {},
    socialRetailMedia: { awareness: 7, purchaseIntent: 2, trial: 6 },
    tv: { awareness: 6, brandFit: 2, purchaseIntent: 1, trial: 2 },
    tvSocial: { awareness: 9, brandFit: 1, purchaseIntent: 2, trial: 4 },
  },
  claim: {
    breakfast: { appeal: 1, brandFit: 3, purchaseIntent: 1 },
    noArtificial: {},
    none: { appeal: -1, brandFit: 1, purchaseIntent: -1, wtp: -0.03 },
    realPeanuts: { appeal: 3, brandFit: 1, purchaseIntent: 2, wtp: 0.05 },
    sourceOfProtein: {
      appeal: -1,
      brandFit: -4,
      purchaseIntent: -1,
      wtp: 0.06,
    },
  },
  packaging: {
    balanced: { appeal: 3, awareness: 1, brandFit: -3, purchaseIntent: 2 },
    nutellaLed: {},
    nutellaOnly: { appeal: -2, awareness: 2, brandFit: 4, purchaseIntent: -2 },
    peanutLed: { appeal: -1, awareness: -3, brandFit: -9, purchaseIntent: -3 },
    peanutOnly: {
      appeal: -3,
      awareness: -6,
      brandFit: -16,
      purchaseIntent: -6,
    },
  },
  packSize: {
    "12": { appeal: -1, purchaseIntent: 1, trial: 3, wtp: -0.35 },
    "16": {},
    "20": { brandFit: -1, purchaseIntent: -3, trial: -3, wtp: 0.3 },
  },
  positioning: {
    family: {},
    indulgence: { appeal: 4, brandFit: 7, purchaseIntent: 3, wtp: 0.08 },
    natural: { appeal: 1, brandFit: -2, purchaseIntent: 1, wtp: 0.12 },
    protein: { appeal: -1, brandFit: -9, purchaseIntent: -2, wtp: 0.1 },
  },
  price: {
    "4.99": {
      appeal: -1,
      brandFit: -3,
      purchaseIntent: 5,
      trial: 4,
      wtp: -0.3,
    },
    "5.49": { purchaseIntent: 3, trial: 2, wtp: -0.1 },
    "5.99": {},
    "6.49": {
      appeal: -1,
      brandFit: 1,
      purchaseIntent: -6,
      trial: -4,
      wtp: 0.2,
    },
  },
  retail: {
    kroger: { awareness: -4, trial: -2 },
    mass: {
      appeal: -2,
      awareness: 9,
      brandFit: -4,
      purchaseIntent: 1,
      trial: 5,
      wtp: -0.15,
    },
    target: { awareness: -2, brandFit: 1, trial: -1, wtp: 0.05 },
    walmart: {},
    walmartTarget: { awareness: 5, purchaseIntent: 1, trial: 3 },
    wholeFoods: {
      appeal: 1,
      awareness: -9,
      brandFit: -1,
      trial: -6,
      wtp: 0.2,
    },
  },
};

const INTERACTIONS: Interaction[] = [
  {
    effect: { brandFit: -3, purchaseIntent: 1 },
    note: "Protein positioning combined with a protein claim reads as a sports-nutrition product: coherent, but it pulls the concept away from Nutella's indulgence DNA.",
    when: { claim: "sourceOfProtein", positioning: "protein" },
  },
  {
    effect: { appeal: -2, brandFit: -3, purchaseIntent: -3 },
    note: "An indulgence story with a protein claim on the front of pack sends a mixed message: buyers do not know whether this is a treat or a better-for-you product.",
    when: { claim: "sourceOfProtein", positioning: "indulgence" },
  },
  {
    effect: { brandFit: 3, purchaseIntent: 2 },
    note: "Indulgence positioning on a Nutella-only pack reads as a true Nutella line extension.",
    when: { packaging: "nutellaOnly", positioning: "indulgence" },
  },
  {
    effect: { appeal: -2, purchaseIntent: -2 },
    note: "A peanut-only pack with an indulgence campaign sends a mixed signal: the ad promises Nutella, the shelf shows peanut butter.",
    when: { packaging: "peanutOnly", positioning: "indulgence" },
  },
  {
    effect: { purchaseIntent: 2, wtp: 0.1 },
    note: "Natural positioning matches Whole Foods shopper expectations and lifts willingness to pay in that channel.",
    when: { positioning: "natural", retail: "wholeFoods" },
  },
  {
    audiences: ["parents"],
    effect: { purchaseIntent: 2, trial: 2 },
    note: "For parents, the 20 oz format with a breakfast claim signals a pantry staple rather than a treat.",
    when: { claim: "breakfast", packSize: "20" },
  },
];

const KPI_IDS: KpiId[] = [
  "appeal",
  "awareness",
  "brandFit",
  "purchaseIntent",
  "trial",
  "wtp",
];

const OZ_FACTOR: Record<string, number> = { "12": 0.9, "16": 1, "20": 1.08 };

/** Brand-fit and trial move less than stated intent: extensions rarely
    score above the high eighties on fit, and first-year trial rates for new
    spreads rarely exceed the low twenties even with heavy support. */
const EFFECT_SCALE: Partial<Record<KpiId, number>> = {
  brandFit: 0.7,
  trial: 0.5,
};

const compiledCache = new Map<AudienceId, CompiledAudience>();

const optimizationCache = new Map<
  AudienceId,
  {
    best: { indices: number[]; kpis: Kpis; score: number };
    scores: Float64Array;
  }
>();

/* ── Prediction ─────────────────────────────────── */

export const predict = (
  configuration: Configuration,
  audience: AudienceId
): Kpis => evaluate(toIndices(configuration), compile(audience));

/* ── Optimization ───────────────────────────────── */

/** Best configuration for the audience on a balanced launch score. */
export const optimize = (
  audience: AudienceId,
  current?: Configuration
): OptimizationResult => {
  const { best, scores } = rankAll(audience);
  let currentRank = 1;
  if (current) {
    const currentScore = launchScore(predict(current, audience));
    for (let i = 0; i < scores.length; i++) {
      if (scores[i] > currentScore) currentRank += 1;
    }
  }
  return {
    configuration: fromIndices(best.indices),
    currentRank,
    kpis: best.kpis,
    scenarioCount: SCENARIO_COUNT,
  };
};

/* ── Formatting ─────────────────────────────────── */

export const formatDelta = (kpi: KpiDefinition, delta: number): string => {
  if (kpi.format === "currency") {
    return `${delta >= 0 ? "+" : "−"}$${Math.abs(delta).toFixed(2)}`;
  }
  return `${signed(Math.round(delta))}\u00A0pts`;
};

export const formatKpi = (kpi: KpiDefinition, value: number): string => {
  if (kpi.format === "currency") return `$${value.toFixed(2)}`;
  if (kpi.format === "percent") return `${Math.round(value)}%`;
  return `${Math.round(value)}/100`;
};

/* ── Private helpers ────────────────────────────── */

/** Soft-compresses 0–100 KPIs near the bounds so extra effects still move the
    score (a hard clamp would make penalties free once a KPI saturates). */
const clamp = (value: number): number => {
  if (value > 85) return 85 + 15 * Math.tanh((value - 85) / 15);
  if (value < 15) return 15 - 15 * Math.tanh((15 - value) / 15);
  return value;
};

const compile = (audience: AudienceId): CompiledAudience => {
  const cached = compiledCache.get(audience);
  if (cached) return cached;
  const baseline = KPI_IDS.map((id) => BASELINES[audience][id]);
  const effects = FACTORS.map((factor) =>
    factor.options.map((option) =>
      toVector(
        EFFECTS[factor.id][option.value],
        AUDIENCE_ADJUSTMENTS[audience][factor.id]?.[option.value]
      )
    )
  );
  const interactions = INTERACTIONS.filter(
    (interaction) =>
      !interaction.audiences || interaction.audiences.includes(audience)
  ).map((interaction) => ({
    effect: toVector(interaction.effect),
    when: Object.entries(interaction.when).map(([factor, value]) => {
      const factorIndex = FACTORS.findIndex((f) => f.id === factor);
      const optionIndex = FACTORS[factorIndex].options.findIndex(
        (o) => o.value === value
      );
      return [factorIndex, optionIndex] as [number, number];
    }),
  }));
  const compiled = { baseline, effects, interactions };
  compiledCache.set(audience, compiled);
  return compiled;
};

const evaluate = (indices: number[], compiled: CompiledAudience): Kpis => {
  const vector = [...compiled.baseline];
  for (let f = 0; f < indices.length; f++) {
    const effect = compiled.effects[f][indices[f]];
    for (let k = 0; k < vector.length; k++) vector[k] += effect[k];
  }
  for (const interaction of compiled.interactions) {
    if (interaction.when.every(([f, o]) => indices[f] === o)) {
      for (let k = 0; k < vector.length; k++)
        vector[k] += interaction.effect[k];
    }
  }
  const kpis: Kpis = {
    appeal: clamp(vector[0]),
    awareness: clamp(vector[1]),
    brandFit: clamp(vector[2]),
    purchaseIntent: clamp(vector[3]),
    revenue: 0,
    trial: clamp(vector[4]),
    wtp: Math.max(0, vector[5]),
  };
  kpis.revenue = revenueIndex(indices, kpis.trial, compiled.baseline[4]);
  return kpis;
};

const fromIndices = (indices: number[]): Configuration => {
  const configuration = { ...BASELINE_CONFIGURATION };
  FACTORS.forEach((factor, i) => {
    configuration[factor.id] = factor.options[indices[i]].value;
  });
  return configuration;
};

/** Purchase intent, weighted with brand fit, appeal, WTP and revenue. */
const launchScore = (kpis: Kpis): number =>
  kpis.purchaseIntent +
  0.25 * (kpis.brandFit - 65) +
  0.1 * (kpis.appeal - 70) +
  0.08 * (kpis.revenue - 100) +
  10 * (kpis.wtp - 5.4);

/** Scores every scenario once per audience; keeps the best. */
const rankAll = (audience: AudienceId) => {
  const cached = optimizationCache.get(audience);
  if (cached) return cached;

  const compiled = compile(audience);
  const scores = new Float64Array(SCENARIO_COUNT);
  const indices = FACTORS.map(() => 0);
  let best = {
    indices: [...indices],
    kpis: evaluate(indices, compiled),
    score: -Infinity,
  };
  for (let n = 0; n < SCENARIO_COUNT; n++) {
    const kpis = evaluate(indices, compiled);
    const score = launchScore(kpis);
    scores[n] = score;
    if (score > best.score) best = { indices: [...indices], kpis, score };
    // Odometer increment over the factor options.
    let f = indices.length - 1;
    while (f >= 0) {
      indices[f] += 1;
      if (indices[f] < FACTORS[f].options.length) break;
      indices[f] = 0;
      f -= 1;
    }
  }
  const result = { best, scores };
  optimizationCache.set(audience, result);
  return result;
};

const revenueIndex = (
  indices: number[],
  trial: number,
  baselineTrial: number
): number => {
  const option = (id: FactorId) => {
    const index = FACTORS.findIndex((factor) => factor.id === id);
    return FACTORS[index].options[indices[index]].value;
  };
  const price = option("price");
  const packSize = option("packSize");
  const retail = option("retail");
  return Math.round(
    100 *
      (trial / baselineTrial) *
      (Number(price) / Number(BASELINE_CONFIGURATION.price)) *
      DISTRIBUTION[retail] *
      OZ_FACTOR[packSize]
  );
};

const signed = (value: number): string =>
  value > 0 ? `+${value}` : value < 0 ? `−${Math.abs(value)}` : "0";

const toIndices = (configuration: Configuration): number[] =>
  FACTORS.map((factor) =>
    Math.max(
      0,
      factor.options.findIndex((o) => o.value === configuration[factor.id])
    )
  );

const toVector = (...effects: (Effect | undefined)[]): number[] =>
  KPI_IDS.map(
    (id) =>
      effects.reduce((sum, effect) => sum + (effect?.[id] ?? 0), 0) *
      (EFFECT_SCALE[id] ?? 1)
  );
